import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { db, type EntityRequest } from '../../services/firebase';
import { Modal } from '../common/Modal';
import { humanizeLookupCollection } from './RequestNewValueModal';
import { RequestDetailPanel } from './RequestDetailPanel';

/**
 * MyRequestsPanel — Brand Portal Phase 2.5 (chunk iii).
 *
 * The brand owner's inbox of entity requests they've submitted (chunk ii) across
 * ALL the brands they're a member of. Lists each request live so an admin's
 * status change or reply surfaces without a reload, and opens a
 * `RequestDetailPanel` (summary + message thread + reply composer) when tapped.
 *
 * Reads are direct Firestore (James's locked decision: callable-only WRITES, but
 * client READS are allowed and gated by rules). To avoid a composite-index
 * dependency, the query is a single-field `where('brandId','in', …)` — ordering
 * by `updatedAt` and the latest-50 cap are applied client-side after the
 * snapshot lands.
 *
 * Deliberately omitted in v1 (flagged in the handoff): per-request unread dots.
 * The status badge + "last updated" relative time carry the signal; the owner
 * opens a request to see what changed. Easy to layer on later.
 */

interface MyRequestsPanelProps {
  /** Every brand the signed-in user holds a membership on (useAuth.ownedBrandIds). */
  userBrandIds: string[];
  /** Optional brandId → display name, for the per-brand chips. Falls back to id. */
  brandNames?: Record<string, string>;
}

// Firestore caps an `in` filter at 30 values. A brand owner with >30 brands is
// not a real case, but slice defensively rather than throw at query time.
const MAX_IN_VALUES = 30;
// Show the most-recent N; the open-request rate limit (20/brand) keeps volumes
// low, so this cap is a safety net rather than real pagination.
const DISPLAY_CAP = 50;

type StatusFilter = 'open' | 'resolved' | 'all';
type TypeFilter = 'all' | 'calibre' | 'lookup';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: 'All types' },
  { key: 'calibre', label: 'Calibre' },
  { key: 'lookup', label: 'Lookup' },
];

const STATUS_BADGE: Record<EntityRequest['status'], { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-700' },
  needs_info: { label: 'Needs info', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const OPEN_STATUSES: EntityRequest['status'][] = ['pending', 'needs_info'];

/** Type chip text: "Calibre" or the humanized lookup collection ("Occasion" …). */
export function requestTypeLabel(req: EntityRequest): string {
  if (req.type === 'calibre') return 'Calibre';
  return humanizeLookupCollection((req.payload as { collection: string }).collection);
}

/** Primary row title: calibre name, or the proposed lookup value. */
export function requestTitle(req: EntityRequest): string {
  if (req.type === 'calibre') {
    return (req.payload as { name?: string }).name || 'Untitled calibre';
  }
  return (req.payload as { value?: string }).value || 'Untitled value';
}

export function StatusBadge({
  status,
  size = 'sm',
}: {
  status: EntityRequest['status'];
  size?: 'sm' | 'lg';
}) {
  const { label, cls } = STATUS_BADGE[status];
  const sizing = size === 'lg' ? 'text-sm px-3 py-1' : 'text-xs px-2 py-0.5';
  return (
    <span className={`inline-block font-medium rounded-full ${sizing} ${cls}`}>
      {label}
    </span>
  );
}

/** "2 hours ago" from a Firestore Timestamp, with a safe fallback. */
export function relativeTime(ts?: { toDate: () => Date }): string {
  if (!ts || typeof ts.toDate !== 'function') return '—';
  try {
    return formatDistanceToNow(ts.toDate(), { addSuffix: true });
  } catch {
    return '—';
  }
}

// ── Row ──────────────────────────────────────────────────────────────────────

function RequestRow({
  request,
  brandLabel,
  showBrand,
  onSelect,
}: {
  request: EntityRequest;
  brandLabel: string;
  showBrand: boolean;
  onSelect: (request: EntityRequest) => void;
}) {
  const subtitle =
    request.type === 'lookup' ? requestTypeLabel(request) : null;

  return (
    <li>
      <button
        type='button'
        onClick={() => onSelect(request)}
        className='w-full flex items-center gap-4 px-6 py-3 text-left hover:bg-gray-50'
      >
        <StatusBadge status={request.status} />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-gray-900 truncate'>
            {requestTitle(request)}
          </p>
          <p className='text-xs text-gray-500 truncate'>
            {request.type === 'calibre' ? 'Calibre' : `Lookup · ${subtitle}`}
          </p>
        </div>
        {showBrand && (
          <span className='text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0 max-w-[10rem] truncate'>
            {brandLabel}
          </span>
        )}
        <span className='text-xs text-gray-400 flex-shrink-0 whitespace-nowrap'>
          {relativeTime(request.updatedAt)}
        </span>
      </button>
    </li>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function MyRequestsPanel({ userBrandIds, brandNames }: MyRequestsPanelProps) {
  const [requests, setRequests] = useState<EntityRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Stable list of queryable brand ids (deduped + capped to Firestore's `in` max).
  const brandIds = useMemo(
    () => Array.from(new Set(userBrandIds)).slice(0, MAX_IN_VALUES),
    [userBrandIds],
  );
  const brandKey = brandIds.join(',');

  // Live subscription. Single-field `in` filter → no composite index; order +
  // cap are applied client-side below.
  useEffect(() => {
    if (brandIds.length === 0) {
      setRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);

    const q = query(
      collection(db, 'entityRequests'),
      where('brandId', 'in', brandIds),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as EntityRequest,
        );
        setRequests(rows);
        setLoading(false);
      },
      () => {
        setError(true);
        setLoading(false);
      },
    );
    return unsubscribe;
    // brandKey captures the brandIds contents; eslint wants the array too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandKey]);

  // Sort (newest-updated first) + cap, then apply the in-memory filters.
  const sorted = useMemo(() => {
    const millis = (r: EntityRequest) => r.updatedAt?.toMillis?.() ?? 0;
    return [...requests].sort((a, b) => millis(b) - millis(a)).slice(0, DISPLAY_CAP);
  }, [requests]);

  const filtered = useMemo(() => {
    return sorted.filter((r) => {
      if (statusFilter === 'open' && !OPEN_STATUSES.includes(r.status)) return false;
      if (statusFilter === 'resolved' && OPEN_STATUSES.includes(r.status)) return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (brandFilter !== 'all' && r.brandId !== brandFilter) return false;
      return true;
    });
  }, [sorted, statusFilter, typeFilter, brandFilter]);

  const openCount = useMemo(
    () => requests.filter((r) => OPEN_STATUSES.includes(r.status)).length,
    [requests],
  );
  const cappedNotice = requests.length > DISPLAY_CAP;
  const showBrand = brandIds.length > 1;
  const brandLabelFor = (id: string) => brandNames?.[id] ?? id;

  const selected = selectedId
    ? requests.find((r) => r.id === selectedId) ?? null
    : null;

  // ── Render ──────────────────────────────────────────────────────────────────

  const noMemberships = brandIds.length === 0;

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
      <div className='px-6 py-4 border-b border-gray-200'>
        <div className='flex items-center justify-between gap-4'>
          <h2 className='text-base font-semibold text-gray-900'>
            Your Requests
            {!noMemberships && openCount > 0 && (
              <span className='ml-2 text-sm font-normal text-gray-400'>
                · {openCount} open
              </span>
            )}
          </h2>
        </div>

        {!noMemberships && (
          <div className='mt-3 flex flex-wrap items-center gap-2'>
            {/* Status filter */}
            <div className='inline-flex rounded-lg border border-gray-300 overflow-hidden'>
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type='button'
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-3 py-1.5 text-sm ${
                    statusFilter === f.key
                      ? 'bg-brand-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              aria-label='Filter by type'
              className='border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
            >
              {TYPE_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>

            {/* Brand filter — only when the user spans multiple brands */}
            {showBrand && (
              <select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                aria-label='Filter by brand'
                className='border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
              >
                <option value='all'>All brands</option>
                {brandIds.map((id) => (
                  <option key={id} value={id}>
                    {brandLabelFor(id)}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {noMemberships ? (
        <div className='px-6 py-10 text-center text-sm text-gray-500'>
          Join a brand portal to make requests.
        </div>
      ) : error ? (
        <div className='px-6 py-10 text-center text-sm text-red-600'>
          Couldn't load your requests. Please try again.
        </div>
      ) : loading ? (
        <div className='px-6 py-10 text-center text-sm text-gray-400'>
          Loading requests…
        </div>
      ) : requests.length === 0 ? (
        <div className='px-6 py-10 text-center text-sm text-gray-500'>
          <p>You haven't requested any new values yet.</p>
          <p className='mt-1 text-xs text-gray-400'>
            When you can't find a calibre or lookup value while editing, tap
            “Request new value” to ask.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className='px-6 py-10 text-center text-sm text-gray-500'>
          No requests match this filter.
        </div>
      ) : (
        <>
          <ul className='divide-y divide-gray-100'>
            {filtered.map((req) => (
              <RequestRow
                key={req.id}
                request={req}
                brandLabel={brandLabelFor(req.brandId)}
                showBrand={showBrand}
                onSelect={(r) => setSelectedId(r.id)}
              />
            ))}
          </ul>
          {cappedNotice && (
            <div className='px-6 py-3 border-t border-gray-100 text-center text-xs text-gray-400'>
              Showing latest {DISPLAY_CAP}
            </div>
          )}
        </>
      )}

      {selected && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedId(null)}
          title='Request details'
          size='lg'
        >
          <RequestDetailPanel
            request={selected}
            brandLabel={showBrand ? brandLabelFor(selected.brandId) : null}
            onClose={() => setSelectedId(null)}
          />
        </Modal>
      )}
    </div>
  );
}
